// CapacityView.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, ToggleButtonGroup, ToggleButton, CircularProgress,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, InputLabel, OutlinedInput, Chip, Checkbox,
  Divider, IconButton, Collapse, Button, Tooltip,
} from '@mui/material';
import ChevronRightIcon    from '@mui/icons-material/ChevronRight';
import CloseIcon           from '@mui/icons-material/Close';
import WarningAmberIcon    from '@mui/icons-material/WarningAmber';
import DownloadIcon        from '@mui/icons-material/Download';
import ArrowDownwardIcon   from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon     from '@mui/icons-material/ArrowUpward';
import UnfoldMoreIcon      from '@mui/icons-material/UnfoldMore';
import PeopleOutlineIcon   from '@mui/icons-material/PeopleOutline';
import { Bar }             from 'react-chartjs-2';
import '../../utils/chartConfig';   // ensures Chart.js components are registered
import { apiFetch }        from '../../utils/api';
import EmployeeNameLink    from '../common/EmployeeNameLink';
import { TABS }            from '../cards/EmployeeExpandedView';
import { Logger }          from '../../utils/logger';

const logger = new Logger('CapacityView');

// ── Constants ────────────────────────────────────────────────────────────────
const HORIZONS = [
  { key: '4',  label: '4W'  },
  { key: '8',  label: '8W'  },
  { key: '13', label: '13W' },
  { key: '26', label: '26W' },
  { key: '39', label: '39W' },
  { key: '52', label: '52W' },
];
const COL_COUNT = 2 + HORIZONS.length;

// ── Colour helpers ────────────────────────────────────────────────────────────
const pctColor = (pct) => {
  if (pct == null) return '#666';
  if (pct >= 85)  return '#CF6679';
  if (pct >= 70)  return '#FFCF44';
  return '#03DAC6';
};
const pctBg = (pct) => {
  if (pct == null) return 'transparent';
  if (pct >= 85)  return 'rgba(207,102,121,0.12)';
  if (pct >= 70)  return 'rgba(255,207,68,0.12)';
  return 'rgba(3,218,198,0.12)';
};
const pctColorRgba = (pct, alpha = 0.75) => {
  if (pct >= 85) return `rgba(207,102,121,${alpha})`;
  if (pct >= 70) return `rgba(255,207,68,${alpha})`;
  return `rgba(3,218,198,${alpha})`;
};

// ── Sorting helpers ───────────────────────────────────────────────────────────
const applySortHorizon = (arr, sort) => {
  if (!sort.col) return arr;
  return [...arr].sort((a, b) => {
    const pA = a.horizons?.[sort.col]?.pct ?? 0;
    const pB = b.horizons?.[sort.col]?.pct ?? 0;
    return sort.dir === 'desc' ? pB - pA : pA - pB;
  });
};

// ── Sub-components ────────────────────────────────────────────────────────────

const PctCell = ({ pct, small = false }) => (
  <TableCell align="center" sx={{
    color: pctColor(pct), fontWeight: 600,
    fontSize: small ? '0.72rem' : '0.8rem',
    bgcolor: pctBg(pct), px: 1, py: small ? 0.5 : 0.75,
  }}>
    {pct != null ? `${pct.toFixed(1)}%` : '—'}
  </TableCell>
);

const SortIcon = ({ active, dir }) => {
  if (!active) return <UnfoldMoreIcon sx={{ fontSize: '0.85rem', opacity: 0.35, ml: 0.25 }} />;
  return dir === 'desc'
    ? <ArrowDownwardIcon sx={{ fontSize: '0.85rem', color: '#BB86FC', ml: 0.25 }} />
    : <ArrowUpwardIcon   sx={{ fontSize: '0.85rem', color: '#BB86FC', ml: 0.25 }} />;
};

const HorizonHeader = ({ firstColLabel, sort, onSort }) => (
  <TableRow>
    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {firstColLabel}
    </TableCell>
    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', width: 60, px: 1 }}>
      People
    </TableCell>
    {HORIZONS.map(h => (
      <TableCell key={h.key} align="center" onClick={() => onSort?.(h.key)}
        sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
          letterSpacing: '0.06em', width: 72, px: 1,
          cursor: onSort ? 'pointer' : 'default',
          userSelect: 'none',
          '&:hover': onSort ? { bgcolor: 'rgba(187,134,252,0.06)' } : {},
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {h.label}
          {onSort && <SortIcon active={sort?.col === h.key} dir={sort?.dir} />}
        </Box>
      </TableCell>
    ))}
  </TableRow>
);

const GroupRow = ({ name, headcount, horizons, highlight = false, badge = null,
  expandable = false, expanded = false, onToggle }) => (
  <TableRow hover onClick={expandable ? onToggle : undefined}
    sx={{ cursor: expandable ? 'pointer' : 'default',
      bgcolor: highlight ? 'rgba(255,255,255,0.03)' : 'inherit',
      '&:hover': expandable ? { bgcolor: 'rgba(255,255,255,0.06)' } : {},
    }}>
    <TableCell sx={{ fontSize: highlight ? '0.85rem' : '0.8rem',
      fontWeight: highlight ? 700 : 400, py: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {expandable && (
          <ChevronRightIcon sx={{ fontSize: '1rem', color: 'text.secondary', flexShrink: 0,
            transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.18s ease' }} />
        )}
        {name}{badge}
      </Box>
    </TableCell>
    <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.8rem', py: 0.75 }}>
      {headcount}
    </TableCell>
    {HORIZONS.map(h => <PctCell key={h.key} pct={horizons?.[h.key]?.pct} />)}
  </TableRow>
);

const EmployeeRow = ({ name, title, horizons, employee_id }) => (
  <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.18)' }}>
    <TableCell sx={{ fontSize: '0.75rem', py: 0.5, pl: 4.5 }}>
      <EmployeeNameLink employee={{ employee_id, name }} tab={TABS.TIMESHEETS}
        style={{ color: 'rgba(255,255,255,0.75)' }}>{name}</EmployeeNameLink>
      {title && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: '0.65rem', lineHeight: 1.2 }}>
          {title}
        </Typography>
      )}
    </TableCell>
    <TableCell sx={{ py: 0.5 }} />
    {HORIZONS.map(h => <PctCell key={h.key} pct={horizons?.[h.key]?.pct} small />)}
  </TableRow>
);

const ExpandableGroup = ({ rowKey, name, headcount, horizons, employees = [],
  highlight = false, badge = null, expanded, onToggle }) => (
  <>
    <GroupRow name={name} headcount={headcount} horizons={horizons}
      highlight={highlight} badge={badge}
      expandable={employees.length > 0} expanded={expanded}
      onToggle={() => onToggle(rowKey)} />
    {expanded && employees.map(emp => (
      <EmployeeRow key={emp.employee_id} name={emp.name} title={emp.title} horizons={emp.horizons} employee_id={emp.employee_id} />
    ))}
  </>
);

// ── Legend ───────────────────────────────────────────────────────────────────
const Legend = () => (
  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
    {[
      { color: '#03DAC6', label: '< 70%  Available'   },
      { color: '#FFCF44', label: '70–84%  Filling up'  },
      { color: '#CF6679', label: '≥ 85%  At capacity'  },
    ].map(({ color, label }) => (
      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Box>
    ))}
  </Box>
);

// ── Horizon summary cards ─────────────────────────────────────────────────────
const SummaryCards = ({ horizons }) => (
  <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
    {HORIZONS.map(h => {
      const pct = horizons?.[h.key]?.pct ?? null;
      const cap = horizons?.[h.key]?.capacity_hours ?? 0;
      const booked = horizons?.[h.key]?.booked_hours ?? 0;
      return (
        <Box key={h.key} sx={{
          flex: 1, minWidth: 110,
          bgcolor: 'background.paper',
          border: '1px solid', borderColor: 'divider',
          borderRadius: 1.5, p: 1.5, textAlign: 'center',
          borderTop: `3px solid ${pctColor(pct)}`,
        }}>
          <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'text.secondary', mb: 0.75 }}>
            {h.label} Out
          </Typography>
          <Typography variant="h5" sx={{ color: pctColor(pct), fontWeight: 700, lineHeight: 1 }}>
            {pct != null ? `${pct.toFixed(0)}%` : '—'}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
            {booked.toLocaleString()}h / {cap.toLocaleString()}h
          </Typography>
        </Box>
      );
    })}
  </Box>
);

// ── Alert banner ──────────────────────────────────────────────────────────────
const AlertBanner = ({ managers, categories }) => {
  if (!managers.length && !categories.length) return null;
  const items = [
    ...managers.map(m => `${m.manager_name} (team)`),
    ...categories.map(c => `${c.name} skills`),
  ];
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2.5, p: 1.5,
      bgcolor: 'rgba(207,102,121,0.1)', border: '1px solid rgba(207,102,121,0.35)',
      borderRadius: 1.5 }}>
      <WarningAmberIcon sx={{ color: '#CF6679', fontSize: '1.1rem', mt: 0.1, flexShrink: 0 }} />
      <Box>
        <Typography variant="body2" sx={{ color: '#CF6679', fontWeight: 600, mb: 0.25 }}>
          At-capacity alert — next 4 weeks
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {items.join(' · ')} {items.length === 1 ? 'is' : 'are'} ≥ 85% booked
        </Typography>
      </Box>
    </Box>
  );
};

// ── Utilization bar chart ─────────────────────────────────────────────────────
const UtilizationChart = ({ managers, categories }) => {
  const [section, setSection]   = useState('team');
  const [horizon, setHorizon]   = useState('4');

  const rows = useMemo(() => {
    const src = section === 'team' ? managers : categories;
    return [...src]
      .map(r => ({ name: r.manager_name ?? r.name, pct: r.horizons?.[horizon]?.pct ?? 0 }))
      .sort((a, b) => b.pct - a.pct);
  }, [section, horizon, managers, categories]);

  const chartData = useMemo(() => ({
    labels: rows.map(r => r.name),
    datasets: [{
      label: 'Utilization %',
      data: rows.map(r => r.pct),
      backgroundColor: rows.map(r => pctColorRgba(r.pct, 0.75)),
      borderColor:     rows.map(r => pctColor(r.pct)),
      borderWidth: 1,
      borderRadius: 3,
    }],
  }), [rows]);

  const chartOptions = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        min: 0, max: 100,
        grid: { color: '#333' },
        ticks: { color: '#aaa', font: { size: 10 }, callback: v => `${v}%` },
      },
      y: {
        grid: { display: false },
        ticks: { color: '#ccc', font: { size: 10 } },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#222', borderColor: '#555', borderWidth: 1,
        callbacks: { label: ctx => ` ${ctx.raw.toFixed(1)}% booked` },
      },
    },
  }), []);

  const chartHeight = Math.max(180, rows.length * 28);

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Capacity Sold Over Time (CSOT™)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ToggleButtonGroup size="small" value={section} exclusive
            onChange={(_, v) => { if (v) setSection(v); }}
            sx={toggleSx}>
            <ToggleButton value="team">By Manager</ToggleButton>
            <ToggleButton value="skills">By Skill Category</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup size="small" value={horizon} exclusive
            onChange={(_, v) => { if (v) setHorizon(v); }}
            sx={toggleSx}>
            {HORIZONS.map(h => (
              <ToggleButton key={h.key} value={h.key}>{h.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Box sx={{ height: chartHeight }}>
        <Bar data={chartData} options={chartOptions} />
      </Box>
    </Paper>
  );
};

// ── Bench panel ───────────────────────────────────────────────────────────────
const BenchPanel = ({ bench, threshold }) => {
  const [open, setOpen] = useState(true);
  const benchList = bench ?? [];
  const thresholdVal = threshold ?? 20;

  return (
    <Paper elevation={0} sx={{ p: 2, mt: 3, bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', cursor: 'pointer', mb: open ? 2 : 0 }}
        onClick={() => setOpen(o => !o)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleOutlineIcon sx={{ color: '#03DAC6', fontSize: '1.1rem' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Available Now
          </Typography>
          <Chip label={benchList.length} size="small"
            sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(3,218,198,0.1)',
              color: '#03DAC6', border: '1px solid rgba(3,218,198,0.35)' }} />
          <Typography variant="caption" color="text.secondary">
            &lt; {thresholdVal}% booked in next 4 weeks
          </Typography>
        </Box>
        <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: '1rem',
          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.18s ease' }} />
      </Box>

      <Collapse in={open}>
        {benchList.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No employees under {thresholdVal}% utilization in the next 4 weeks
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', width: 160 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', width: 160 }}>Manager</TableCell>
                  {HORIZONS.map(h => (
                    <TableCell key={h.key} align="center"
                      sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', width: 64, px: 1 }}>
                      {h.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {benchList.map(emp => (
                  <TableRow key={emp.employee_id} hover>
                    <TableCell sx={{ fontSize: '0.8rem', py: 0.75 }}>
                      <EmployeeNameLink employee={emp} tab={TABS.TIMESHEETS}>{emp.name}</EmployeeNameLink>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', py: 0.75 }}>{emp.title || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', py: 0.75 }}>{emp.manager}</TableCell>
                    {HORIZONS.map(h => <PctCell key={h.key} pct={emp.horizons?.[h.key]?.pct} small />)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Paper>
  );
};

// ── Shared toggle style ───────────────────────────────────────────────────────
const toggleSx = {
  '& .MuiToggleButton-root': {
    color: 'text.secondary', borderColor: '#424242',
    px: 1.5, fontSize: '0.72rem', textTransform: 'none', py: 0.5,
  },
  '& .MuiToggleButton-root.Mui-selected': {
    color: '#BB86FC', borderColor: '#BB86FC', bgcolor: 'rgba(187,134,252,0.1)',
  },
};

// ── CSV export ────────────────────────────────────────────────────────────────
const buildCSV = (data, categories) => {
  const hKeys = HORIZONS.map(h => h.key);
  const header = ['Type', 'Name', 'People', ...HORIZONS.map(h => `${h.label}%`)];

  const row = (type, name, headcount, horizons) =>
    [type, `"${name}"`, headcount, ...hKeys.map(k => horizons?.[k]?.pct?.toFixed(1) ?? '')];

  const rows = [
    header,
    row('Company', 'Total', data.company.headcount, data.company.horizons),
    [],
    ...data.by_manager.map(m => row('Manager', m.manager_name, m.headcount, m.horizons)),
    [],
    ...categories.map(c => row('Skill Category', c.name, c.eligible_headcount, c.horizons)),
  ];

  if (data.bench?.length) {
    rows.push([]);
    rows.push(['Bench', 'Name', 'Title', 'Manager', ...HORIZONS.map(h => `${h.label}%`)]);
    data.bench.forEach(e =>
      rows.push(['Bench', `"${e.name}"`, `"${e.title}"`, `"${e.manager}"`,
        ...hKeys.map(k => e.horizons?.[k]?.pct?.toFixed(1) ?? '')])
    );
  }

  return rows.map(r => r.join(',')).join('\n');
};

const downloadCSV = (data, categories, view) => {
  const csv  = buildCSV(data, categories);
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `capacity_${view}_${data.as_of}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};


// ════════════════════════════════════════════════════════════════════════════
// Main component
// ════════════════════════════════════════════════════════════════════════════
const CapacityView = () => {
  const [view, setView]         = useState('booked');
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const [expanded, setExpanded]               = useState(new Set());
  const [teamSort, setTeamSort]               = useState({ col: null, dir: 'desc' });
  const [skillSort, setSkillSort]             = useState({ col: null, dir: 'desc' });
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSkills, setSelectedSkills]   = useState([]);

  const toggleExpanded = useCallback((key) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const handleSort = useCallback((section, col) => {
    const setter = section === 'team' ? setTeamSort : setSkillSort;
    setter(prev =>
      prev.col === col
        ? { col, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { col, dir: 'desc' }
    );
  }, []);

  // Fetch
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setExpanded(new Set());
      try {
        const res  = await apiFetch(`capacity?view=${view}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        logger.error('Error loading capacity data:', err);
        if (!cancelled) setError('Failed to load capacity data. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [view]);

  // Derived
  const categories     = useMemo(() => data?.skills?.categories ?? [], [data]);
  const allIndividual  = useMemo(() => data?.skills?.individual  ?? [], [data]);
  const categoryNames  = useMemo(() => [...new Set(categories.map(c => c.name))].sort(), [categories]);

  const filteredSkills = useMemo(() => {
    if (!selectedCategory) return allIndividual;
    return allIndividual.filter(s => s.category === selectedCategory);
  }, [allIndividual, selectedCategory]);

  const skillNames = useMemo(() => [...new Set(filteredSkills.map(s => s.name))].sort(), [filteredSkills]);

  const selectedSkillRows = useMemo(() => {
    const rows = selectedSkills
      .map(n => allIndividual.find(s => s.name === n))
      .filter(Boolean);
    if (skillSort.col) {
      return rows.sort((a, b) => {
        const pA = a.horizons?.[skillSort.col]?.pct ?? 0;
        const pB = b.horizons?.[skillSort.col]?.pct ?? 0;
        return skillSort.dir === 'desc' ? pB - pA : pA - pB;
      });
    }
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedSkills, allIndividual, skillSort]);

  const sortEmpsByHorizon = useCallback((employees, sort) => {
    if (!sort.col || !employees?.length) return employees ?? [];
    return [...employees].sort((a, b) => {
      const pA = a.horizons?.[sort.col]?.pct ?? 0;
      const pB = b.horizons?.[sort.col]?.pct ?? 0;
      return sort.dir === 'desc' ? pB - pA : pA - pB;
    });
  }, []);

  const sortedManagers = useMemo(() => {
    const sorted = applySortHorizon(data?.by_manager ?? [], teamSort);
    if (!teamSort.col) return sorted;
    return sorted.map(mgr => ({
      ...mgr,
      employees: sortEmpsByHorizon(mgr.employees, teamSort),
    }));
  }, [data, teamSort, sortEmpsByHorizon]);

  const sortedCategories = useMemo(() => {
    const sorted = applySortHorizon(
      categories.filter(c => !selectedCategory || c.name === selectedCategory),
      skillSort
    );
    if (!skillSort.col) return sorted;
    return sorted.map(cat => ({
      ...cat,
      employees: sortEmpsByHorizon(cat.employees, skillSort),
    }));
  }, [categories, selectedCategory, skillSort, sortEmpsByHorizon]);

  // Alerts — at-capacity in 4 weeks
  const alerts = useMemo(() => {
    if (!data) return { managers: [], categories: [] };
    return {
      managers:   (data.by_manager ?? []).filter(m => (m.horizons?.['4']?.pct ?? 0) >= 85),
      categories: categories.filter(c => (c.horizons?.['4']?.pct ?? 0) >= 85),
    };
  }, [data, categories]);

  const handleSkillChange = (e) => {
    const val = e.target.value;
    setSelectedSkills(typeof val === 'string' ? val.split(',') : val);
  };
  const removeSkill = (n) => setSelectedSkills(prev => prev.filter(s => s !== n));

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Capacity Sold Over Time (CSOT™)
          </Typography>
          {data?.as_of && (
            <Typography variant="caption" color="text.secondary">
              As of {data.as_of} · {data.company?.headcount ?? 0} eligible employees
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Legend />
          {data && (
            <Tooltip title="Export to CSV">
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
                onClick={() => downloadCSV(data, categories, view)}
                sx={{ borderColor: '#424242', color: 'text.secondary', fontSize: '0.75rem',
                  textTransform: 'none', '&:hover': { borderColor: '#BB86FC', color: '#BB86FC' } }}>
                Export CSV
              </Button>
            </Tooltip>
          )}
          <ToggleButtonGroup size="small" value={view} exclusive
            onChange={(_, v) => { if (v) setView(v); }} sx={toggleSx}>
            <ToggleButton value="booked">Booked</ToggleButton>
            <ToggleButton value="planned">Planned</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* ── Loading / error ─────────────────────────────────────────── */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
          minHeight: 300, gap: 2 }}>
          <CircularProgress size={28} />
          <Typography color="text.secondary">Calculating capacity…</Typography>
        </Box>
      )}
      {!loading && error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {!loading && !error && data && (
        <>
          {/* ── Alerts ──────────────────────────────────────────────── */}
          <AlertBanner managers={alerts.managers} categories={alerts.categories} />

          {/* ── Horizon summary cards ────────────────────────────────── */}
          <SummaryCards horizons={data.company.horizons} />

          {/* ══ Bench — Available Now ══════════════════════════════════ */}
          <BenchPanel bench={data.bench} threshold={data.bench_threshold} />

          {/* ── Utilization bar chart ────────────────────────────────── */}
          <UtilizationChart managers={data.by_manager ?? []} categories={categories} />

          {/* ══ Team Capacity table ═══════════════════════════════════ */}
          <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider' }}>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Team Capacity Sold Over Time (CSOT™)
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                  click row to expand · click horizon to sort
                </Typography>
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <HorizonHeader firstColLabel="Group / Manager"
                    sort={teamSort} onSort={(col) => handleSort('team', col)} />
                </TableHead>
                <TableBody>
                  <GroupRow name="Company Total" headcount={data.company.headcount}
                    horizons={data.company.horizons} highlight />
                  <TableRow>
                    <TableCell colSpan={COL_COUNT} sx={{ p: 0, border: 0 }}>
                      <Divider />
                    </TableCell>
                  </TableRow>
                  {sortedManagers.map(mgr => (
                    <ExpandableGroup key={mgr.manager_name}
                      rowKey={`mgr:${mgr.manager_name}`}
                      name={mgr.manager_name} headcount={mgr.headcount}
                      horizons={mgr.horizons} employees={mgr.employees ?? []}
                      expanded={expanded.has(`mgr:${mgr.manager_name}`)}
                      onToggle={toggleExpanded} />
                  ))}
                  {sortedManagers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={COL_COUNT} align="center"
                        sx={{ py: 4, color: 'text.secondary' }}>
                        No manager data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* ══ Skills Capacity ═══════════════════════════════════════ */}
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider' }}>

            {/* Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Skills Capacity Sold Over Time (CSOT™)
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                    click row to expand · click horizon to sort
                  </Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {data.skills?.threshold_note}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel sx={{ fontSize: '0.8rem' }}>Filter by Category</InputLabel>
                  <Select value={selectedCategory}
                    onChange={e => setSelectedCategory(e.target.value)}
                    label="Filter by Category" sx={{ fontSize: '0.8rem' }}>
                    <MenuItem value=""><em>All Categories</em></MenuItem>
                    {categoryNames.map(c => (
                      <MenuItem key={c} value={c} sx={{ fontSize: '0.8rem' }}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 260, maxWidth: 420 }}>
                  <InputLabel sx={{ fontSize: '0.8rem' }}>Add Skills to Compare</InputLabel>
                  <Select multiple value={selectedSkills} onChange={handleSkillChange}
                    input={<OutlinedInput label="Add Skills to Compare" />}
                    disabled={skillNames.length === 0}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(val => (
                          <Chip key={val} label={val} size="small"
                            onDelete={() => removeSkill(val)}
                            onMouseDown={e => e.stopPropagation()}
                            sx={{ height: 20, fontSize: '0.65rem',
                              bgcolor: 'rgba(3,218,198,0.1)', color: '#03DAC6',
                              border: '1px solid rgba(3,218,198,0.4)',
                              '& .MuiChip-deleteIcon': { color: '#03DAC6', fontSize: '0.75rem' } }} />
                        ))}
                      </Box>
                    )}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                    sx={{ fontSize: '0.8rem' }}>
                    {skillNames.map(s => (
                      <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                        <Checkbox checked={selectedSkills.includes(s)} size="small"
                          sx={{ p: 0.5, mr: 0.5, color: '#03DAC6', '&.Mui-checked': { color: '#03DAC6' } }} />
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedSkills.length > 0 && (
                  <IconButton size="small" onClick={() => setSelectedSkills([])}
                    sx={{ color: 'text.secondary' }}>
                    <CloseIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                )}
              </Box>
            </Box>

            {/* Selected skills */}
            {selectedSkillRows.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  fontWeight: 600, fontSize: '0.65rem' }}>
                  Selected Skills
                </Typography>
                <TableContainer sx={{ mt: 0.5 }}>
                  <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                      <HorizonHeader firstColLabel="Skill"
                        sort={skillSort} onSort={(col) => handleSort('skills', col)} />
                    </TableHead>
                    <TableBody>
                      {selectedSkillRows.map(row => (
                        <ExpandableGroup key={row.name}
                          rowKey={`sel:${row.name}`}
                          name={row.name} headcount={row.eligible_headcount}
                          horizons={row.horizons} employees={row.employees ?? []}
                          highlight
                          badge={
                            <Chip label={row.category} size="small" sx={{
                              height: 16, fontSize: '0.6rem',
                              bgcolor: 'rgba(187,134,252,0.12)', color: '#BB86FC',
                              border: '1px solid rgba(187,134,252,0.35)',
                              '& .MuiChip-label': { px: 0.75 }, ml: 0.5,
                            }} />
                          }
                          expanded={expanded.has(`sel:${row.name}`)}
                          onToggle={toggleExpanded} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider sx={{ mt: 2, mb: 2 }} />
              </Box>
            )}

            {/* Categories */}
            <Typography variant="caption" sx={{ color: 'text.secondary',
              textTransform: 'uppercase', letterSpacing: '0.06em',
              fontWeight: 600, fontSize: '0.65rem' }}>
              By Category
            </Typography>
            <TableContainer sx={{ mt: 0.5 }}>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <HorizonHeader firstColLabel="Skill Category"
                    sort={skillSort} onSort={(col) => handleSort('skills', col)} />
                </TableHead>
                <TableBody>
                  {sortedCategories.map(cat => (
                    <ExpandableGroup key={cat.name}
                      rowKey={`cat:${cat.name}`}
                      name={cat.name} headcount={cat.eligible_headcount}
                      horizons={cat.horizons} employees={cat.employees ?? []}
                      expanded={expanded.has(`cat:${cat.name}`)}
                      onToggle={toggleExpanded} />
                  ))}
                  {sortedCategories.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={COL_COUNT} align="center"
                        sx={{ py: 4, color: 'text.secondary' }}>
                        No skill data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

        </>
      )}
    </Box>
  );
};

export default CapacityView;
